import React, { Fragment, useMemo, useState } from 'react';
import { Add, MoreVert } from '@material-ui/icons';
import { IDataTableColumn } from 'react-data-table-component';
import { Title } from 'src/components/ui/components/forms/title';
import { Panel, PanelType } from 'src/components/ui/components/panel';
import { SearchInput, SearchInputType } from 'src/components/ui/components/forms/inputs/search';
import { INumerated } from 'src/store/properties/models/numerated.interface';
import { COLORS } from 'src/components/ui/theme/variables/colors';
import { Button, ButtonVariant } from 'src/components/ui/components/buttons/button';
import { Table } from 'src/components/ui/components/table';
import { useHistory } from 'react-router-dom';
import { RoutesEnum } from 'src/components/routing/routes';
import { ArchiveIcon, EditIcon, SettingsOutlinedIcon } from 'src/components/ui/icons';
import { useFollowUp } from 'src/store/admin-settings/hooks/use-followup';

import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { guid } from '@datorama/akita';
import { IFollowUp } from 'src/store/admin-settings/models/followup.interface';

import styles from '../../../admin.module.scss';
import baseStyles from 'src/styles/base.module.scss';
import { useAdminSearch } from 'src/store/admin-settings/hooks/use-admin-search';
import { AdminSearchType, adminSettingsService } from 'src/store/admin-settings/admin-settings.service';
import { panelMobileStyles } from 'src/components/ui/main-layout/layout';
import { TitleHolder } from 'src/components/containers/properties/pages/list';
import { useObservable } from '@libreact/use-observable';
import { adminSettingsQuery } from 'src/store/admin-settings/admin-settings.query';

const ITEM_HEIGHT = 48;

export const conditionalRowStyles = [
    {
        // actively renting & i % 2 === 0
        when: (row: INumerated & any) => row.i % 2 === 0,
        style: {
            backgroundColor: COLORS.BACKGROUND_GRAY,
        },
    }
];

export const MoreOptions = ({ itemId }: any) => {
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.SyntheticEvent) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const moreOptions = [
        // {
        //     name: 'View Follow Up',
        //     icon: '',
        //     onClick: () => history.push(`/admin/settings/followup/${itemId}`),
        // },
        {
            name: 'Edit Follow Up',
            icon: <EditIcon />,
            onClick: () => history.push(`/admin/settings/followup/${itemId}/edit`),
        },
        {
            name: 'Archive Follow Up',
            icon: <ArchiveIcon />,
            onClick: () => adminSettingsService.deleteFollowUp(itemId),
        },
    ];

    return (
        <div className={styles.moreOptionsBtn}>
            <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleClick}
            >
                <MoreVert />
            </IconButton>
            <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        maxHeight: ITEM_HEIGHT * 4.5,
                        width: '22ch',
                    },
                }}
            >
                {moreOptions.map((option) => (
                    <MenuItem key={guid()} onClick={option.onClick}>
                        <span className={styles.moreIcon}>{option.icon}</span>
                        <span className={styles.moreName}>{option.name}</span>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}

const triggersMap = new Map();
triggersMap.set('EVENT_TYPE', 'Event Type');
triggersMap.set('PROSPECT_STATUS_UPDATE', 'Prospect Status Update');

const eventTypesMap = new Map();
eventTypesMap.set('MEETING', 'Meeting');
eventTypesMap.set('SHOWING', 'Showing');
eventTypesMap.set('OTHER', 'Other');

const hourTypesMap = new Map();
hourTypesMap.set('AFTER', 'After');
hourTypesMap.set('BEFORE', 'Before');

const receiverTypesMap = new Map();
receiverTypesMap.set('PROSPECTS', 'Prospects');
receiverTypesMap.set('TENANTS', 'Tenants');
receiverTypesMap.set('AGENTS', 'Agents');
receiverTypesMap.set('OWNERS', 'Owners');

const columns: IDataTableColumn<Partial<IFollowUp>>[] = [
    {
        name: 'Trigger',
        selector: 'trigger',
        sortable: true,
        minWidth: '200px',
        maxWidth: '200px',
        cell: (row: Partial<IFollowUp>) => {
            return (
                <TitleHolder style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{ triggersMap.get(row.trigger) }</TitleHolder>
            );
        }
    },
    {
        name: 'Event Type',
        selector: 'eventType',
        sortable: true,
        minWidth: '150px',
        cell: (row: Partial<IFollowUp>) => {
            if (row.trigger === "PROSPECT_STATUS_UPDATE") {
                return null;
            }
            
            return (
                <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{ eventTypesMap.get(row.eventType) }</div>
            );
        }
    },
    {
        name: 'Notification time',
        selector: 'hours',
        sortable: true,
        minWidth: '150px',
        cell: (row: Partial<IFollowUp>) => {
            return (
                <div>{ `${row.hours} Hours ${hourTypesMap.get(row.hourType)}` }</div>
            );
        }
    },
    {
        name: 'Receiver',
        selector: 'receivers',
        sortable: true,
        minWidth: '150px',
        cell: (row: Partial<IFollowUp>) => {
            if (row.receivers?.length) {
                return (
                    <div>{ row.receivers.map((receiver, idx) => {
                        if (row.receivers?.length === 1) {
                            return receiverTypesMap.get(receiver);
                        }

                        if (idx === row.receivers?.length! - 1) {
                            return receiverTypesMap.get(receiver);
                        } else {
                            return `${receiverTypesMap.get(receiver)}, `;
                        }
                    }) }</div>
                );
            }

            return null;
        }
    },
    {
        name: 'Template',
        selector: 'templateId',
        sortable: true,
        minWidth: '150px',
        cell: (row) => {
            return (
                <div>{ row.template?.name }</div>
            );
        }
    },
    {
        name: '',
        selector: '',
        sortable: false,
        minWidth: '100px',
        maxWidth: '100px',
        cell: (row: Partial<IFollowUp>) => <MoreOptions itemId={row.id} />
    },
];

interface IFollowUpSettingsProps {
    loading?: boolean;
    isMobile?: boolean;
}

export const FollowUpsSettings: React.FC<IFollowUpSettingsProps> = ({ isMobile }) => {
    // const [selectedCount, setSelectedCount] = useState(0);
    const history = useHistory();
    const [followUps, isLoading, total] = useFollowUp(); 
    const [, changeSearch] = useAdminSearch(AdminSearchType.FOLLOW_UPS);
    const [isUpdating] = useObservable(adminSettingsQuery.isUpdating$);

    const prepearedFollows = useMemo(() => {
        // const nList = [
        //     {
        //         eventType: 'Showing',
        //         time: '1 hours after',
        //         receiver: 'All Tenants',
        //         template: 'Showing scheduled',
        //     },
        //     {
        //         eventType: 'Meeting',
        //         time: '3 hours before',
        //         receiver: 'Group: 5 Lexingtone Avenue',
        //         template: 'Maintenance planned',
        //     }
        // ];

        if (Array.isArray(followUps) && followUps.length) {
            const sortedFollowUps = followUps.slice().sort((a: any, b: any) => b.id - a.id);
            // if (search) {
            //     return followUps
            //         .filter(item => item.eventType?.toLowerCase().includes(search))
            // }
    
            return sortedFollowUps;
        }

        return [];
    }, [followUps]);

    // const handleSelectionChange = (data: any) => {
    //     setSelectedCount(data.selectedCount);
    // }

    // const changeSearch = (q: string) => {
    //     setSearch(q);
    // }

    return (
        <Fragment>
            <Panel additionalStyles={
                isMobile ? {
                    ...panelMobileStyles,
                    margin: '-22px 0 16px',
                    width: '100%',
                    borderRadius: '0 0 0 10px',
                } : { margin: '-5px 0 30px', borderRadius: '0 0 10px 10px' }
            }>
                <Title
                    icon={<SettingsOutlinedIcon />}
                    iconStyle={{ fontSize: '14px', width: '30px', height: '30px' }}
                    text="Auto Follow Ups"
                />
            </Panel>
            <Panel type={PanelType.EMPTY} additionalStyles={{ margin: '0 0 15px' }}>
                {
                    isMobile
                        ? (
                            <div className={baseStyles.save_bar_mobile}>
                                <Button
                                    icon={<Add style={{fontSize: 14}}/>}
                                    btnVariant={ButtonVariant.PRIMARY}
                                    onClick={() => history.push(RoutesEnum.ADMIN_ADD_FOLLOW_UP)}
                                    text="Add Follow Up"
                                />
                            </div>
                        ) : null
                }
                <div className={styles.actionBarHolder}>
                    <div className={baseStyles.list_action_bar_left}>
                        <SearchInput
                            onChange={changeSearch}
                            inputType={SearchInputType.PRIMARY}
                            placeholderText="Search..."
                        />
                    </div>
                    {
                        !isMobile
                            ? (
                                <div>
                                    <Button
                                        icon={<Add style={{fontSize: 14}}/>}
                                        btnVariant={ButtonVariant.PRIMARY}
                                        onClick={() => history.push(RoutesEnum.ADMIN_ADD_FOLLOW_UP)}
                                        text="Add Follow Up"
                                    />
                                </div>
                            )
                            : null
                    }
                </div>
            </Panel>
            {/* {selectedCount > 0 && <Panel additionalStyles={{ margin: '-5px 0 22px' }}>
                <div className={styles.list_action_bar}>
                    <div className={styles.list_action_bar_left}>
                        {`${selectedCount} Selected`}
                    </div>
                </div>
            </Panel>} */}
            <Panel noPadding sideBorder additionalStyles={{ margin: '0 0 30px' }}>
                <Table<Partial<IFollowUp>>
                    // onSelectionChange={handleSelectionChange}
                    // onRowClicked={onRowClicked}'
                    pagination={false}
                    totalRecordsNumber={total}
                    columns={columns}
                    conditionalRowStyles={conditionalRowStyles}
                    // onSort={onSort}
                    loading={(!prepearedFollows.length && isLoading) || isUpdating}
                    data={prepearedFollows}
                    highlightRow={['name']}
                />
            </Panel>
        </Fragment>
    );
}